import {
  MenuItemProps,
  PolymorphicComponentProps,
  Menu as ToggleMenu,
} from '@mantine/core'
import { Children, forwardRef, Ref, useMemo } from 'react'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { Theme } from '../../theme'
import { ButtonText } from '../button-text'
import { Icon } from '../icon'
import { Tooltip } from '../tooltip'
import {
  ConditionalTooltipProps,
  MenuProps,
  SectionItemProps,
} from './menu.types'
import { Spinner } from '../spinner'

const StyledMenuWrapper = styled(ToggleMenu.Dropdown)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadii.medium};
    background: ${theme.colors.white};
    box-shadow: 0px 8px 32px rgba(33, 37, 53, 0.16);
    margin: ${theme.sizes.xxs} 0;
    padding: 0 ${theme.sizes.xs};
    outline: none;
    position: absolute;
    z-index: 1;
    background-color: white;
    right: 0;
    width: max-content;
    border: none;

    & > button: {
      padding: 0;
    }
  `}
`

const StyledSection = styled.div<{ $hasSectionDivider?: boolean }>`
  ${({ theme, $hasSectionDivider }) => css`
    outline: none;
    padding: ${theme.sizes.xs} 0;

    ${$hasSectionDivider &&
    css`
      border-top: 1px solid ${theme.colors.gray80};
    `}
  `}
`

const MenuSeparator = styled.div`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.colors.gray80};
    margin: ${theme.sizes.xxs} 0;
  `}
`

const ToggleMenuItem = forwardRef<
  HTMLButtonElement,
  PolymorphicComponentProps<'button', MenuItemProps>
>(({ children, ...props }, ref) => (
  <ToggleMenu.Item ref={ref} component="button" {...props}>
    {children}
  </ToggleMenu.Item>
))

const sharedMenuItemsCss = ({ theme }: { theme: Theme }) => css`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${theme.sizes.xs};
  width: 100%;
  height: ${theme.sizes.l};
  padding: 0 ${theme.sizes.xs};
  border-radius: ${theme.borderRadii.small};
  transition: all ${theme.transitionTime} ease;
  font-size: ${theme.fontSizes.bodySmall};
  outline: none;

  .mantine-Menu-itemLabel {
    display: flex;
    align-items: center;
    gap: ${theme.sizes.xs};
    font-weight: 500;
  }

  &:hover {
    cursor: pointer;
  }

  &:hover,
  &:active {
    background: ${theme.colors.gray70};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const StyledMenuItem = styled(ToggleMenuItem)`
  ${sharedMenuItemsCss}
`

const StyledMenuLink = styled(Link)`
  ${sharedMenuItemsCss}
`

const ConditionalTooltip = forwardRef<
  HTMLAnchorElement,
  ConditionalTooltipProps
>(({ content, children, isVisible, ...props }, ref) => {
  if (!isVisible || !content) return <span ref={ref}>{children}</span>

  return (
    <Tooltip ref={ref} content={content} {...props}>
      {children}
    </Tooltip>
  )
})

const SectionItem = forwardRef<HTMLAnchorElement, SectionItemProps>(
  (props, ref) => {
    const {
      keyValue,
      label,
      shouldBeDisabled,
      icon,
      onSelect,
      isLoading = false,
      withSeparator = false,
      dataTestId,
    } = props

    const sectionIcon = icon && <Icon name={icon} size={5} color="gray400" />

    if (props.as === 'link')
      return (
        <>
          {withSeparator && <MenuSeparator />}
          <StyledMenuLink
            data-testid={dataTestId ?? 'menu-link-item'}
            to={props.to}
            ref={ref}
          >
            {isLoading && <Spinner size="s" />}
            {sectionIcon} {label}
          </StyledMenuLink>
        </>
      )

    const handleClick = () => {
      props.onClick?.()
      onSelect?.(keyValue)
    }

    return (
      <>
        {withSeparator && <MenuSeparator />}
        <StyledMenuItem
          data-testid={dataTestId ?? 'menu-button-item'}
          ref={ref as Ref<HTMLButtonElement>}
          disabled={shouldBeDisabled || isLoading}
          onClick={handleClick}
        >
          {isLoading && <Spinner size="s" />}
          {sectionIcon} {label}
        </StyledMenuItem>
      </>
    )
  }
)

const Menu = ({
  title,
  disabled,
  disabledTooltipMessage,
  sections,
  onSelect,
  customTriggerComponent,
  children,
  onChange,
  position = 'bottom-end',
  renderWhenEmpty = false,
}: MenuProps) => {
  const visibleSections = useMemo(
    () => sections?.filter((section) => section.items.length > 0) ?? [],
    [sections]
  )

  const triggerComponent = useMemo(() => {
    if (customTriggerComponent) {
      return customTriggerComponent
    }

    return <ButtonText disabled={disabled}>{title}</ButtonText>
  }, [customTriggerComponent, disabled, title])

  const hasContent =
    visibleSections.length > 0 || Children.toArray(children).length > 0

  if (!renderWhenEmpty && !hasContent) return null

  return (
    <ToggleMenu onChange={onChange} position={position} offset={2}>
      <ToggleMenu.Target>
        {disabled && disabledTooltipMessage ? (
          <Tooltip content={disabledTooltipMessage} placement={position}>
            <span>{triggerComponent}</span>
          </Tooltip>
        ) : (
          triggerComponent
        )}
      </ToggleMenu.Target>

      <StyledMenuWrapper>
        {visibleSections.map((section, sectionIndex) => (
          <StyledSection
            data-testid="menu-section"
            $hasSectionDivider={sectionIndex > 0}
            key={`${section}-${sectionIndex}`}
          >
            {section.items.map((props) => {
              const tooltipContent = props.shouldBeDisabled
                ? props.disabledTooltipInfo
                : props.tooltipInfo

              return (
                <ConditionalTooltip
                  isVisible={!!tooltipContent}
                  content={tooltipContent}
                  key={props.key}
                >
                  <SectionItem
                    onSelect={onSelect}
                    keyValue={props.key}
                    {...props}
                  />
                </ConditionalTooltip>
              )
            })}
          </StyledSection>
        ))}
        {children}
      </StyledMenuWrapper>
    </ToggleMenu>
  )
}

Menu.Section = StyledSection

Menu.Item = StyledMenuItem
Menu.Link = StyledMenuLink

export { Menu }
